/**
 * AdminCustomPrograms - Gestión de rutinas personalizadas
 * 
 * Features:
 * - Lista de rutinas personalizadas y plantillas
 * - Crear/editar/duplicar rutinas
 * - Asignar a clientes premium
 * - Ver progreso del cliente
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Users,
  Dumbbell,
  Copy,
  Edit,
  Trash2,
  CheckCircle2,
  XCircle,
  Calendar,
  Target,
  Home,
  Building2,
  ArrowLeft,
  LayoutTemplate,
  User,
  ChevronRight,
  Loader2
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OBJECTIVES = {
  hypertrophy: { label: "Hipertrofia", color: "bg-purple-500/20 text-purple-400" },
  strength: { label: "Fuerza", color: "bg-red-500/20 text-red-400" },
  definition: { label: "Definición", color: "bg-blue-500/20 text-blue-400" },
  health: { label: "Salud", color: "bg-green-500/20 text-green-400" },
  performance: { label: "Rendimiento", color: "bg-orange-500/20 text-orange-400" },
  rehabilitation: { label: "Rehabilitación", color: "bg-teal-500/20 text-teal-400" }
};

const LOCATIONS = {
  gym: { label: "Gimnasio", icon: Building2 },
  home: { label: "Casa", icon: Home },
  mixed: { label: "Mixto", icon: Dumbbell }
};

const EQUIPMENT = [
  { value: "none", label: "Sin material" },
  { value: "dumbbells", label: "Mancuernas" },
  { value: "bands", label: "Bandas elásticas" },
  { value: "bench", label: "Banco" },
  { value: "barbell", label: "Barra" },
  { value: "pullup_bar", label: "Barra dominadas" },
  { value: "kettlebell", label: "Kettlebell" },
  { value: "cable", label: "Poleas" },
  { value: "machines", label: "Máquinas" },
  { value: "trx", label: "TRX" },
  { value: "other", label: "Otro" }
];

const AdminCustomPrograms = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("programs");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Data
  const [programs, setPrograms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [premiumClients, setPremiumClients] = useState([]);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    client_id: "",
    objective: "hypertrophy",
    location: "gym",
    days_per_week: 3,
    equipment_available: [],
    coach_notes: ""
  });
  
  const [duplicateData, setDuplicateData] = useState({
    new_name: "",
    client_id: ""
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [programsRes, templatesRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/api/custom-programs`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/custom-programs/templates`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/premium-clients`, { headers: getAuthHeaders() })
      ]);
      
      setPrograms(programsRes.data.programs || []);
      setTemplates(templatesRes.data.templates || []);
      setPremiumClients(clientsRes.data.clients || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create program/template
  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    
    if (!isCreatingTemplate && !formData.client_id) {
      toast.error("Selecciona un cliente");
      return;
    }
    
    try {
      const endpoint = isCreatingTemplate 
        ? `${API_URL}/api/custom-programs/templates`
        : `${API_URL}/api/custom-programs`;
      
      const payload = {
        ...formData,
        is_template: isCreatingTemplate,
        client_id: isCreatingTemplate ? null : formData.client_id
      };
      
      await axios.post(endpoint, payload, { headers: getAuthHeaders() });
      
      toast.success(isCreatingTemplate ? "Plantilla creada" : "Rutina creada");
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error creating:", error);
      toast.error(error.response?.data?.detail || "Error al crear");
    }
  };

  // Duplicate program
  const handleDuplicate = async () => {
    if (!duplicateData.new_name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    
    try {
      const params = new URLSearchParams({
        new_name: duplicateData.new_name
      });
      
      if (duplicateData.client_id) {
        params.append("client_id", duplicateData.client_id);
      }
      
      await axios.post(
        `${API_URL}/api/custom-programs/${selectedProgram.id}/duplicate?${params.toString()}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      toast.success("Rutina duplicada");
      setShowDuplicateDialog(false);
      setSelectedProgram(null);
      setDuplicateData({ new_name: "", client_id: "" });
      fetchData();
    } catch (error) {
      console.error("Error duplicating:", error);
      toast.error(error.response?.data?.detail || "Error al duplicar");
    }
  };

  // Delete program
  const handleDelete = async (programId) => {
    if (!window.confirm("¿Eliminar esta rutina? Esta acción no se puede deshacer.")) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/custom-programs/${programId}`, {
        headers: getAuthHeaders()
      });
      toast.success("Rutina eliminada");
      fetchData();
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Error al eliminar");
    }
  };

  // Toggle active
  const handleToggleActive = async (program) => {
    try {
      await axios.put(
        `${API_URL}/api/custom-programs/${program.id}`,
        { is_active: !program.is_active },
        { headers: getAuthHeaders() }
      );
      toast.success(program.is_active ? "Rutina desactivada" : "Rutina activada");
      fetchData();
    } catch (error) {
      console.error("Error toggling:", error);
      toast.error("Error al cambiar estado");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      client_id: "",
      objective: "hypertrophy",
      location: "gym",
      days_per_week: 3,
      equipment_available: [],
      coach_notes: ""
    });
    setIsCreatingTemplate(false);
  };

  // Filter data
  const filteredPrograms = programs.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.client?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Render program card
  const ProgramCard = ({ program, isTemplate = false }) => {
    const objective = OBJECTIVES[program.objective] || OBJECTIVES.hypertrophy;
    const location = LOCATIONS[program.location] || LOCATIONS.gym;
    const LocationIcon = location.icon;
    
    return (
      <Card className="bg-[#1A1A1A] border-[#2A2A2A] hover:border-[#3A3A3A] transition-colors">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-white truncate">{program.name}</h3>
                {!isTemplate && (
                  program.is_active ? (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                      Activa
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30 text-xs">
                      Inactiva
                    </Badge>
                  )
                )}
                {isTemplate && (
                  <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30 text-xs">
                    Plantilla
                  </Badge>
                )}
              </div>
              
              {!isTemplate && program.client && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {program.client.email}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 mb-3">
            <Badge className={objective.color + " text-xs"}>
              <Target className="w-3 h-3 mr-1" />
              {objective.label}
            </Badge>
            <Badge className="bg-[#2A2A2A] text-white text-xs">
              <LocationIcon className="w-3 h-3 mr-1" />
              {location.label}
            </Badge>
            <Badge className="bg-[#2A2A2A] text-white text-xs">
              <Calendar className="w-3 h-3 mr-1" />
              {program.days_per_week} días/sem
            </Badge>
            {program.days_count !== undefined && (
              <Badge className="bg-[#2A2A2A] text-white text-xs">
                {program.days_count} días creados
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-2 pt-2 border-t border-[#2A2A2A]">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/admin/custom-programs/${program.id}`)}
              className="flex-1 border-[#2A2A2A] text-white hover:bg-[#2A2A2A]"
            >
              <Edit className="w-3 h-3 mr-1" />
              Editar
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSelectedProgram(program);
                setDuplicateData({ new_name: `${program.name} (Copia)`, client_id: "" });
                setShowDuplicateDialog(true);
              }}
              className="border-[#2A2A2A] text-white hover:bg-[#2A2A2A]"
            >
              <Copy className="w-3 h-3" />
            </Button>
            {!isTemplate && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleToggleActive(program)}
                className={program.is_active 
                  ? "border-red-500/30 text-red-400 hover:bg-red-500/10"
                  : "border-green-500/30 text-green-400 hover:bg-green-500/10"
                }
              >
                {program.is_active ? <XCircle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleDelete(program.id)}
              className="text-red-400 hover:bg-red-500/10"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#0B0B0B]/95 backdrop-blur border-b border-[#1A1A1A] px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/admin")}
              className="text-white hover:bg-[#1A1A1A]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-heading font-bold text-[#D4AF37]">
                Entrenos Personalizados
              </h1>
              <p className="text-xs text-muted-foreground">
                {programs.length} rutinas · {templates.length} plantillas
              </p>
            </div>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowCreateDialog(true);
            }}
            className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold"
          >
            <Plus className="w-4 h-4 mr-1" />
            Nueva
          </Button>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre o cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-[#1A1A1A] border-[#2A2A2A] text-white"
          />
        </div>
      </div>
      
      {/* Tabs */}
      <div className="p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 bg-[#1A1A1A]">
            <TabsTrigger value="programs" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B0B0B]">
              <Users className="w-4 h-4 mr-2" />
              Rutinas ({programs.length})
            </TabsTrigger>
            <TabsTrigger value="templates" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B0B0B]">
              <LayoutTemplate className="w-4 h-4 mr-2" />
              Plantillas ({templates.length})
            </TabsTrigger>
            <TabsTrigger value="clients" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B0B0B]">
              <User className="w-4 h-4 mr-2" />
              Clientes ({premiumClients.length})
            </TabsTrigger>
          </TabsList>
          
          {/* Programs Tab */}
          <TabsContent value="programs" className="mt-4 space-y-3">
            {filteredPrograms.length === 0 ? (
              <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
                <CardContent className="p-8 text-center">
                  <Dumbbell className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No hay rutinas personalizadas</p>
                  <Button
                    onClick={() => {
                      resetForm();
                      setIsCreatingTemplate(false);
                      setShowCreateDialog(true);
                    }}
                    className="mt-4 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
                  >
                    Crear primera rutina
                  </Button>
                </CardContent>
              </Card>
            ) : (
              filteredPrograms.map(program => (
                <ProgramCard key={program.id} program={program} />
              ))
            )}
          </TabsContent>
          
          {/* Templates Tab */}
          <TabsContent value="templates" className="mt-4 space-y-3">
            {filteredTemplates.length === 0 ? (
              <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
                <CardContent className="p-8 text-center">
                  <LayoutTemplate className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No hay plantillas</p>
                  <Button
                    onClick={() => {
                      resetForm();
                      setIsCreatingTemplate(true);
                      setShowCreateDialog(true);
                    }}
                    className="mt-4 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
                  >
                    Crear primera plantilla
                  </Button>
                </CardContent>
              </Card>
            ) : (
              filteredTemplates.map(template => (
                <ProgramCard key={template.id} program={template} isTemplate />
              ))
            )}
          </TabsContent>
          
          {/* Clients Tab */}
          <TabsContent value="clients" className="mt-4 space-y-3">
            {premiumClients.map(client => (
              <Card key={client.id} className="bg-[#1A1A1A] border-[#2A2A2A]">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-white">{client.name || client.email}</p>
                      <p className="text-sm text-muted-foreground">{client.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {client.has_active_program ? (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          {client.active_program_name}
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">
                          Sin rutina
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          resetForm();
                          setFormData(prev => ({ ...prev, client_id: client.id }));
                          setIsCreatingTemplate(false);
                          setShowCreateDialog(true);
                        }}
                        className="text-[#D4AF37]"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37]">
              {isCreatingTemplate ? "Nueva Plantilla" : "Nueva Rutina Personalizada"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Type toggle */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant={isCreatingTemplate ? "outline" : "default"}
                onClick={() => setIsCreatingTemplate(false)}
                className={!isCreatingTemplate ? "bg-[#D4AF37] text-[#0B0B0B]" : "border-[#2A2A2A]"}
              >
                <User className="w-4 h-4 mr-2" />
                Para Cliente
              </Button>
              <Button
                type="button"
                variant={isCreatingTemplate ? "default" : "outline"}
                onClick={() => setIsCreatingTemplate(true)}
                className={isCreatingTemplate ? "bg-[#D4AF37] text-[#0B0B0B]" : "border-[#2A2A2A]"}
              >
                <LayoutTemplate className="w-4 h-4 mr-2" />
                Plantilla
              </Button>
            </div>
            
            {/* Name */}
            <div>
              <Label>Nombre de la rutina</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Hipertrofia 4 días"
                className="bg-[#0B0B0B] border-[#2A2A2A] text-white"
              />
            </div>
            
            {/* Client select (if not template) */}
            {!isCreatingTemplate && (
              <div>
                <Label>Cliente Premium</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                >
                  <SelectTrigger className="bg-[#0B0B0B] border-[#2A2A2A] text-white">
                    <SelectValue placeholder="Seleccionar cliente" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                    {premiumClients.map(client => (
                      <SelectItem key={client.id} value={client.id} className="text-white">
                        {client.name || client.email}
                        {client.has_active_program && " (Ya tiene rutina)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Objective */}
            <div>
              <Label>Objetivo</Label>
              <Select
                value={formData.objective}
                onValueChange={(value) => setFormData({ ...formData, objective: value })}
              >
                <SelectTrigger className="bg-[#0B0B0B] border-[#2A2A2A] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  {Object.entries(OBJECTIVES).map(([key, obj]) => (
                    <SelectItem key={key} value={key} className="text-white">
                      {obj.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Location */}
            <div>
              <Label>Lugar de entrenamiento</Label>
              <Select
                value={formData.location}
                onValueChange={(value) => setFormData({ ...formData, location: value })}
              >
                <SelectTrigger className="bg-[#0B0B0B] border-[#2A2A2A] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  {Object.entries(LOCATIONS).map(([key, loc]) => (
                    <SelectItem key={key} value={key} className="text-white">
                      {loc.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Days per week */}
            <div>
              <Label>Días por semana</Label>
              <Select
                value={formData.days_per_week.toString()}
                onValueChange={(value) => setFormData({ ...formData, days_per_week: parseInt(value) })}
              >
                <SelectTrigger className="bg-[#0B0B0B] border-[#2A2A2A] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  {[1, 2, 3, 4, 5, 6, 7].map(n => (
                    <SelectItem key={n} value={n.toString()} className="text-white">
                      {n} {n === 1 ? "día" : "días"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Notes */}
            <div>
              <Label>Notas generales (opcional)</Label>
              <Textarea
                value={formData.coach_notes}
                onChange={(e) => setFormData({ ...formData, coach_notes: e.target.value })}
                placeholder="Indicaciones generales, restricciones, objetivos..."
                className="bg-[#0B0B0B] border-[#2A2A2A] text-white min-h-[80px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              className="border-[#2A2A2A] text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
            >
              Crear y configurar días
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Duplicate Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37]">
              Duplicar Rutina
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre de la nueva rutina</Label>
              <Input
                value={duplicateData.new_name}
                onChange={(e) => setDuplicateData({ ...duplicateData, new_name: e.target.value })}
                className="bg-[#0B0B0B] border-[#2A2A2A] text-white"
              />
            </div>
            
            <div>
              <Label>Asignar a cliente (opcional)</Label>
              <Select
                value={duplicateData.client_id}
                onValueChange={(value) => setDuplicateData({ ...duplicateData, client_id: value })}
              >
                <SelectTrigger className="bg-[#0B0B0B] border-[#2A2A2A] text-white">
                  <SelectValue placeholder="Dejar como plantilla" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  <SelectItem value="" className="text-white">
                    Dejar como plantilla
                  </SelectItem>
                  {premiumClients.map(client => (
                    <SelectItem key={client.id} value={client.id} className="text-white">
                      {client.name || client.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Si asignas a un cliente, se desactivarán sus otras rutinas
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDuplicateDialog(false)}
              className="border-[#2A2A2A] text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDuplicate}
              className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
            >
              <Copy className="w-4 h-4 mr-2" />
              Duplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomPrograms;
